import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { MessageCircle, Phone, Mail } from "lucide-react";
import AppHeader from "../components/AppHeader";
import "./Account.css";

export default function AccountResponses() {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const isRTL = i18n.language?.startsWith('ar');
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/me/responses`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        if (response.ok) {
          const json = await response.json();
          setData(json);
        } else {
          setError(t('accoresp.failedToLoadResponses'));
        }
      } catch (e) {
        setError(t('accoresp.failedToLoadResponses'));
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchResponses();
    } else {
      setLoading(false);
      setError(t('accoresp.pleaseLogIn'));
    }
  }, [token, isRTL]);

  const getChannelIcon = (channel) => {
    switch (channel?.toLowerCase()) {
      case 'whatsapp':
      case 'phone':
        return <Phone size={14} />;
      case 'email':
        return <Mail size={14} />;
      default:
        return <MessageCircle size={14} />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(t('accoreco.enus'), {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="account-page" style={{ minHeight: '100vh', paddingBottom: 100 }}>
      <AppHeader
        title={t('accoresp.responses')}
        backFallback="/account"
        testId="account-responses-header"
      />

      <div style={{ padding: 16, maxWidth: 600, margin: '0 auto' }}>
        {error && (
          <div style={{ 
            padding: 12, 
            borderRadius: 12, 
            background: '#FEF2F2', 
            color: '#DC2626',
            marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ height: 80, background: '#F3F4F6', borderRadius: 16, animation: 'pulse 2s infinite' }} />
            <div style={{ height: 200, background: '#F3F4F6', borderRadius: 16, animation: 'pulse 2s infinite' }} />
          </div>
        ) : data ? (
          <div style={{ 
            padding: 16, 
            borderRadius: 16, 
            background: '#fff', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #E5E7EB',
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              marginBottom: 16,
            }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
                {t('accoresp.inbox')}
              </h3>
              <span style={{ 
                fontSize: 13, 
                color: '#6B7280',
                background: '#F3F4F6',
                padding: '4px 10px',
                borderRadius: 999,
              }}>
                {data.total ?? 0}
              </span>
            </div>

            {(data.items || []).length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: 32, 
                color: '#6B7280',
              }}>
                <MessageCircle size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
                <p style={{ margin: 0, fontSize: 14 }}>
                  {t('accoresp.noResponsesYet')}
                </p>
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#9CA3AF' }}>
                  {t('accoresp.whenBuyersContactYouMessages')}
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {data.items.map((msg, idx) => (
                  <div 
                    key={msg.id || idx} 
                    style={{ 
                      padding: 14,
                      borderRadius: 12,
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: 8,
                    }}>
                      <span style={{ fontWeight: 500, fontSize: 14 }}>
                        {msg.from_name || (t('accoresp.buyer'))}
                      </span>
                      <span style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 4,
                        fontSize: 12, 
                        color: '#6B7280',
                        background: '#E5E7EB',
                        padding: '2px 8px',
                        borderRadius: 999,
                      }}>
                        {getChannelIcon(msg.channel)}
                        {msg.channel || 'message'}
                      </span>
                    </div>
                    
                    <p style={{ 
                      margin: 0, 
                      fontSize: 14, 
                      color: '#374151',
                      lineHeight: 1.5,
                    }}>
                      {msg.message}
                    </p>
                    
                    <div style={{ 
                      marginTop: 10, 
                      fontSize: 12, 
                      color: '#9CA3AF',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}>
                      {msg.ad_title && (
                        <span 
                          style={{ cursor: 'pointer', color: '#F97316' }}
                          onClick={() => msg.ad_id && navigate(`/ad/${msg.ad_id}`)}
                        >
                          {msg.ad_title}
                        </span>
                      )}
                      {msg.created_at && (
                        <span>• {formatDate(msg.created_at)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
